import React from 'react';
import { Link } from 'react-router-dom';
import { 
  CalendarIcon, 
  UserGroupIcon, 
  MapPinIcon, 
  ClockIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { formatLocation } from '../../../utils/formatters';

const BookingSummary = ({ tour, travelers, date, totalPrice }) => {
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not selected';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Tour Image */}
      <div className="relative">
        <img 
          src={tour.image} 
          alt={tour.title} 
          className="w-full h-48 object-cover"
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
          <h3 className="text-white font-bold text-xl">{tour.title}</h3>
          <div className="flex items-center text-white text-sm">
            <MapPinIcon className="h-4 w-4 mr-1" />
            <span>{formatLocation(tour.location)}</span>
          </div>
        </div>
      </div>
      
      {/* Booking Details */}
      <div className="p-6">
        <h4 className="font-semibold text-gray-800 mb-4">Booking Summary</h4>
        
        <div className="space-y-4 mb-6">
          {/* Tour Duration */}
          <div className="flex items-start">
            <ClockIcon className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Duration</p>
              <p className="font-medium">{tour.duration} {tour.duration === 1 ? 'day' : 'days'}</p>
            </div>
          </div>
          
          {/* Tour Date */}
          <div className="flex items-start">
            <CalendarIcon className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Date</p>
              <p className="font-medium">{formatDate(date)}</p>
            </div>
          </div>
          
          {/* Travelers */}
          <div className="flex items-start">
            <UserGroupIcon className="h-5 w-5 text-gray-500 mr-3 mt-0.5" />
            <div>
              <p className="text-sm text-gray-500">Travelers</p>
              <p className="font-medium">{travelers} {travelers === 1 ? 'person' : 'people'}</p>
            </div>
          </div>
        </div>
        
        {/* Price Breakdown */}
        <div className="border-t border-gray-200 pt-4 mb-6">
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Tour Price</span>
            <span>${typeof tour.price === 'number' ? tour.price : 0} × {travelers}</span>
          </div>
          <div className="border-t border-gray-200 my-2 pt-2 flex justify-between font-semibold">
            <span>Total</span>
            <span>${typeof totalPrice === 'number' ? totalPrice : 0}</span>
          </div>
        </div>
        
        {/* Cancellation Policy */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h5 className="font-medium text-gray-800 mb-2">Cancellation Policy</h5>
          <p className="text-sm text-gray-600 mb-2">
            Free cancellation up to 7 days before the tour date. After that, a 50% fee applies.
          </p>
          <Link to="/cancellation-policy" className="text-sm text-primary hover:underline">
            View full policy
          </Link>
        </div>
        
        {/* Secure Booking */}
        <div className="flex items-center justify-center text-sm text-gray-500">
          <ShieldCheckIcon className="h-4 w-4 mr-1" />
          <span>Secure payment & booking</span>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;
